import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { IProjectResultResponse } from '../../MACRA_1/api/IApiResponse';
import { FlowTypeMapLabel } from '../../MACRA_1/enum/General';
import { DischargeType, DischargeTypeMapLabel, Shape, SideOrientation } from '../../MACRA_1/enum/HydraulicData';
import { IProjectMacra1 } from '../../MACRA_1/store/project/projectSlice';
import { conversionLabel } from '../../conversion';
import { IProjectInfoForm } from '../../pages/ProjectInfo/IProjectInfo';
import arial from '../../style/Arial/arial.ttf';
import ProjectInformation from './PDFComponents/ProjectInformation';
import PDFFooter from './PDFLayout/PDFFooter';
import PDFHeader from './PDFLayout/PDFHeader';
import { styles } from './PDFStyle';
import PDFTable from './PDFTableMaterial/PDFTableMaterial';

interface IPDFProps {
    project: IProjectMacra1;
    projectInfo: IProjectInfoForm;
    results: IProjectResultResponse;
    channelDesignImg: string;
    dischargeCurveImg: string;
}

Font.register({
    family: 'Arial',
    fonts: [
        {
            src: arial,
        },
    ],
});

const PDF = (props: IPDFProps) => {
    const { t, i18n } = useTranslation();
    const { project, projectInfo, results, dischargeCurveImg, channelDesignImg } = props;

    let dischargeAndFreeboard, inputDischarge, discharge, side;

    if (project.hydraulicData!.dischargeType === DischargeType.Automated) {
        //dischargeAndFreeboard = `${DischargeTypeMapLabel[project.data!.hydraulicData!.dischargeType]}, ${t('pdf_freeboard_satisfied')}`;
        inputDischarge = t('hydraulic_data_automated_discharge');
        discharge = results.generalResult.general.discharge.toFixed(2);
    } else {
        //dischargeAndFreeboard = t('pdf_freeboard_satisfied');
        inputDischarge = t('channel_item_discharge', { unit_measure: conversionLabel.m3s_ft3s[projectInfo?.unitOfMeasure as number] });
        discharge = results ? project.hydraulicData!.discharge : 0;
    }

    if (project.hydraulicData?.shape == Shape.Curved) {
        side = t('right');
        if (project.hydraulicData?.sideOrientation == SideOrientation.Left) {
            side = t('left');
        }
    }

    return (
        <Document>
            <Page size='A4' orientation='landscape' style={styles.page}>
                <PDFHeader projectInfo={projectInfo} />
                <View style={styles.body}>
                    <View style={[styles.rowSpaceBetween]}>
                        <ProjectInformation projectInfo={projectInfo} width='65%' />
                        <View style={{ width: '35%' }}>
                            <Text style={styles.title}>{t('pdf_input_title')}</Text>
                            <View style={styles.padding5}>
                                {project.hydraulicData?.dischargeType === DischargeType.Manual && (
                                    <View style={[styles.rowSpaceBetween, styles.rowColor]}>
                                        <Text>{inputDischarge}</Text>
                                        <Text>{project.hydraulicData!.discharge.toFixed(2)}</Text>
                                    </View>
                                )}
                                <View style={styles.rowSpaceBetween}>
                                    <Text>{t('general_results_slope', { unit_measure: '%' })}</Text>
                                    <Text>{project.hydraulicData!.longitudinalSlope.toFixed(2)}</Text>
                                </View>
                                <View style={[styles.rowSpaceBetween, styles.rowColor]}>
                                    <Text>{t('general_results_freeboard', { unit_measure: conversionLabel.m_ft[projectInfo?.unitOfMeasure as number] })}</Text>
                                    <Text>{project.hydraulicData!.freeboard.toFixed(2)}</Text>
                                </View>
                                <View style={styles.rowSpaceBetween}>
                                    <Text>{t('pdf_radius', { unit_measure: conversionLabel.m_ft[projectInfo?.unitOfMeasure as number] })}</Text>
                                    <Text>{project.hydraulicData?.shape === Shape.Curved ? project.hydraulicData!.radius.toFixed(2) : ''}</Text>
                                </View>
                                <View style={[styles.rowSpaceBetween, styles.rowColor]}>
                                    <Text>{t('pdf_side')}</Text>
                                    <Text>{side}</Text>
                                </View>
                            </View>
                        </View>
                    </View>

                    <Text style={styles.boldGreen}>{t('graphical_results_channel_design')}</Text>
                    <View style={{ ...styles.box, padding: 0, gap: 3, height: '280' }}>{!!channelDesignImg && <Image src={channelDesignImg} />}</View>

                    <Text style={styles.title} break>
                        {t('pdf_results')}
                    </Text>
                    <View style={styles.rowSpaceBetween}>
                        <View style={styles.section35}>
                            <View style={[styles.rowSpaceBetween, styles.rowColor]}>
                                <Text>{t('general_results_freeboard', { unit_measure: conversionLabel.m_ft[projectInfo?.unitOfMeasure as number] })}</Text>
                                <Text>{results.generalResult.general.freeboard.toFixed(2)}</Text>
                            </View>
                            <View style={styles.rowSpaceBetween}>
                                <Text>{t('general_results_slope', { unit_measure: '%' })}</Text>
                                <Text>{results.generalResult.general.longitudinalSlope.toFixed(2)}</Text>
                            </View>
                            <View style={[styles.rowSpaceBetween, styles.rowColor]}>
                                <Text>{t('general_results_water', { unit_measure: conversionLabel.m_ft[projectInfo?.unitOfMeasure as number] })}</Text>
                                <Text>{results.generalResult.general.waterDepth.toFixed(2)}</Text>
                            </View>
                            <View style={styles.rowSpaceBetween}>
                                <Text>{t('general_results_froude')}</Text>
                                <Text>{results.generalResult.general.froudeNumber.toFixed(2)}</Text>
                            </View>
                        </View>
                        <View style={styles.section65}>
                            <Text style={styles.rowColor}>
                                {DischargeTypeMapLabel()[project.hydraulicData!.dischargeType]}, {t('pdf_freeboard_satisfied')}
                            </Text>
                            <Text>{'-'}</Text>
                            <Text style={styles.rowColor}>{'-'}</Text>
                            <Text>{FlowTypeMapLabel()[results.generalResult.general.flowType]}</Text>
                        </View>
                    </View>

                    <View style={[styles.row, { marginLeft: '25%', marginRight: '7%' }]}>
                        <Text style={styles.boldGreen}>{t('pdf_total')}</Text>
                        <Text style={styles.boldGreen}>{t('general_results_left_overbank')}</Text>
                        <Text style={styles.boldGreen}>{t('general_results_main_channel')}</Text>
                        <Text style={styles.boldGreen}>{t('general_results_right_overbank')}</Text>
                    </View>
                    <View style={styles.rowSpaceBetween}>
                        <View style={styles.section35}>
                            <View style={[styles.rowSpaceBetween, styles.rowColor]}>
                                <Text>{t('channel_item_discharge', { unit_measure: conversionLabel.m3s_ft3s[projectInfo?.unitOfMeasure as number] })}</Text>
                                <Text>{discharge}</Text>
                            </View>
                            <View style={styles.rowSpaceBetween}>
                                <Text>{t('channel_item_cross_area', { unit_measure: conversionLabel.m2_ft2[projectInfo?.unitOfMeasure as number] })}</Text>
                                <Text>{results.generalResult.general.crossSectionalArea.toFixed(2)}</Text>
                            </View>
                            <View style={[styles.rowSpaceBetween, styles.rowColor]}>
                                <Text>{t('channel_item_perimeter', { unit_measure: conversionLabel.m_ft[projectInfo?.unitOfMeasure as number] })}</Text>
                                <Text>{results.generalResult.general.wettedPerimeter.toFixed(2)}</Text>
                            </View>
                            <View style={styles.rowSpaceBetween}>
                                <Text>{t('channel_item_radius', { unit_measure: conversionLabel.m_ft[projectInfo?.unitOfMeasure as number] })}</Text>
                                <Text>{results.generalResult.general.hydraulicRadius.toFixed(2)}</Text>
                            </View>
                            <View style={[styles.rowSpaceBetween, styles.rowColor]}>
                                <Text>{t('channel_item_velocity', { unit_measure: conversionLabel.ms_fts[projectInfo?.unitOfMeasure as number] })}</Text>
                                <Text>{results.generalResult.general.avarageWaterVelocity.toFixed(2)}</Text>
                            </View>
                            <View style={styles.rowSpaceBetween}>
                                <Text>{t('channel_item_roughness')}</Text>
                                <Text></Text>
                            </View>
                        </View>
                        <View style={[styles.section65, styles.row]}>
                            <View style={styles.sector}>
                                <Text style={styles.rowColor}>{results.generalResult.leftOverbank.discharge.toFixed(2)}</Text>
                                <Text>{results.generalResult.leftOverbank.crossSectionalArea.toFixed(2)}</Text>
                                <Text style={styles.rowColor}>{results.generalResult.leftOverbank.wettedPerimeter.toFixed(2)}</Text>
                                <Text>{results.generalResult.leftOverbank.hydraulicRadius.toFixed(2)}</Text>
                                <Text style={styles.rowColor}>{results.generalResult.leftOverbank.avarageWaterVelocity.toFixed(2)}</Text>
                                <Text>{results.generalResult.leftOverbank.avarageRoughness.toFixed(4)}</Text>
                            </View>
                            <View style={styles.sector}>
                                <Text style={styles.rowColor}>{results.generalResult.mainChannel.discharge.toFixed(2)}</Text>
                                <Text>{results.generalResult.mainChannel.crossSectionalArea.toFixed(2)}</Text>
                                <Text style={styles.rowColor}>{results.generalResult.mainChannel.wettedPerimeter.toFixed(2)}</Text>
                                <Text>{results.generalResult.mainChannel.hydraulicRadius.toFixed(2)}</Text>
                                <Text style={styles.rowColor}>{results.generalResult.mainChannel.avarageWaterVelocity.toFixed(2)}</Text>
                                <Text>{results.generalResult.mainChannel.avarageRoughness.toFixed(4)}</Text>
                            </View>
                            <View style={styles.sector}>
                                <Text style={styles.rowColor}>{results.generalResult.rightOverbank.discharge.toFixed(2)}</Text>
                                <Text>{results.generalResult.rightOverbank.crossSectionalArea.toFixed(2)}</Text>
                                <Text style={styles.rowColor}>{results.generalResult.rightOverbank.wettedPerimeter.toFixed(2)}</Text>
                                <Text>{results.generalResult.rightOverbank.hydraulicRadius.toFixed(2)}</Text>
                                <Text style={styles.rowColor}>{results.generalResult.rightOverbank.avarageWaterVelocity.toFixed(2)}</Text>
                                <Text>{results.generalResult.rightOverbank.avarageRoughness.toFixed(4)}</Text>
                            </View>
                        </View>
                    </View>
                    <Text style={styles.note}>{t('pdf_notes')}</Text>

                    <View style={styles.py20}>
                        <PDFTable results={results} projectInfo={projectInfo} project={project} />
                    </View>

                    <Text style={styles.boldGreen} break>
                        {t('graphical_results_discharge_curve')}
                    </Text>
                    <View style={{ ...styles.box, height: '280px' }}>{!!dischargeCurveImg && <Image src={dischargeCurveImg} />}</View>
                    <View style={styles.disclaimerBox}>
                        <View style={styles.disclaimerTitleBox}>
                            <View style={styles.greenRectangleDisclaimer}></View> <Text style={{ paddingLeft: '3px' }}>{t('pdf_disclaimer')}</Text>
                        </View>
                        <Text>{t('pdf_disclaimer_body')}</Text>
                    </View>
                </View>
                <PDFFooter />
            </Page>
        </Document>
    );
};

export default PDF;
